import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { 
  validateAddress as validateAddressAction,
  analyzeAddress as analyzeAddressAction,
  traceTransactions as traceTransactionsAction,
  setCurrentAddress,
  setCurrentBlockchain,
  clearError,
  clearCurrentResults,
  clearSearchHistory,
  clearAllData
} from '../../store/slices/kytSlice';
import { branding } from '../../constants/branding';
import TransactionFlowDiagram from './TransactionFlowDiagram';
import CryptoTransactionFlow from './CryptoTransactionFlow';
import { pdfExportService } from '../../services/pdfExportService';
import PDFChartRenderer from './PDFChartRenderer';
import KYTTransactionHistory from './KYTTransactionHistory';
import KYTTraceSteps from './KYTTraceSteps';
import { kytApiService } from '../../services/adminApiService';
import './AdminComponents.css';

interface BlockchainInfo {
  name: string;
  displayName: string;
  symbol: string;
  addressPrefix: string;
}

interface AddressValidation {
  address: string;
  blockchain: string;
  isValid: boolean;
  detectedBlockchain: string | null;
}

interface KYTAnalysis {
  address: string;
  blockchain: string;
  addressType: string;
  riskScore: number;
  riskLevel: string;
  riskFactors: Record<string, any>;
  isSanctioned: boolean;
  isExchange: boolean;
  exchangeName?: string;
  totalReceived: number;
  totalSent: number;
  netBalance: number;
  transactionCount: number;
  firstSeen: string;
  lastUpdated: string;
  transactions: any[];
  analysisTimestamp: string;
}

interface TransactionTrace {
  traceId: string;
  rootAddress: string;
  blockchain: string;
  maxSteps: number;
  actualSteps: number;
  totalAmount: number;
  riskScore: number;
  traceStatus: string;
  startTime: string;
  endTime: string;
  traceSteps: any[];
  highRiskAddresses: string[];
  exchangeAddresses: string[];
  sanctionsAddresses: string[];
  summary: any;
}

const KYTDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    currentAddress,
    currentBlockchain,
    isValidating,
    isAnalyzing,
    isTracing,
    validation,
    analysis,
    trace,
    progress,
    error,
    searchHistory
  } = useSelector((state: RootState) => state.kyt);

  const [activeTab, setActiveTab] = useState('analyze');
  const [diagramTab, setDiagramTab] = useState('spider'); // 'spider' or 'flow'
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [lastExportTime, setLastExportTime] = useState(0);
  const [showPDFRenderer, setShowPDFRenderer] = useState(false);
  const [maxSteps, setMaxSteps] = useState<number>(7);
  const [includeZeroValue, setIncludeZeroValue] = useState<boolean>(false);
  const [blockchains] = useState<BlockchainInfo[]>([
    { name: 'ETHEREUM', displayName: 'Ethereum', symbol: 'ETH', addressPrefix: '0x' },
    { name: 'BITCOIN', displayName: 'Bitcoin', symbol: 'BTC', addressPrefix: '1' },
    { name: 'STELLAR', displayName: 'Stellar', symbol: 'XLM', addressPrefix: 'G' },
    { name: 'CARDANO', displayName: 'Cardano', symbol: 'ADA', addressPrefix: 'addr1' },
    { name: 'RIPPLE', displayName: 'Ripple', symbol: 'XRP', addressPrefix: 'r' }
  ]);

  // Lightweight client-side blockchain detection to improve UX
  const detectBlockchainFromAddress = (address: string): string | null => {
    const trimmed = (address || '').trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('0x') && trimmed.length === 42) return 'ETHEREUM';
    if (trimmed.startsWith('1') || trimmed.startsWith('3') || trimmed.toLowerCase().startsWith('bc1')) return 'BITCOIN';
    if (trimmed.startsWith('G') && trimmed.length === 56) return 'STELLAR';
    if (trimmed.startsWith('addr1')) return 'CARDANO';
    if (trimmed.startsWith('r') && trimmed.length === 34) return 'RIPPLE';
    return null;
  };

  const handleValidateAddress = async () => {
    if (!currentAddress.trim()) return;
    
    dispatch(clearError());
    await dispatch(validateAddressAction({
      address: currentAddress,
      blockchain: currentBlockchain
    }));
  };

  const handleAnalyzeAddress = async () => {
    if (!currentAddress.trim() || !validation?.isValid) {
      return;
    }

    dispatch(clearError());
    await dispatch(analyzeAddressAction({
      address: currentAddress,
      blockchain: currentBlockchain
    }));
  };

  const handleTraceTransactions = async () => {
    if (!currentAddress.trim()) return;

    dispatch(clearError());
    
    // First, validate the address
    await dispatch(validateAddressAction({
      address: currentAddress,
      blockchain: currentBlockchain
    }));

    // Then, analyze the address
    await dispatch(analyzeAddressAction({
      address: currentAddress,
      blockchain: currentBlockchain
    }));

    // Pre-trace: Light-touch only. Let backend handle blockchain-specific fallbacks
    try {
      if (currentBlockchain === 'BITCOIN') {
        console.log('Pre-trace: recalculating Bitcoin amounts...');
        await kytApiService.recalculateBitcoinAmounts();
      } else if (currentBlockchain === 'RIPPLE') {
        console.log('Pre-trace: fetching Ripple data...');
        await kytApiService.fetchRippleData(currentAddress);
      }
    } catch (prepError) {
      console.warn('Pre-trace preparation warning:', prepError);
    }

    // Finally, trace the transactions
    await dispatch(traceTransactionsAction({
      address: currentAddress,
      blockchain: currentBlockchain,
      maxSteps: maxSteps,
      includeZeroValue
    }));
  };

  // PDF Export function - Hybrid approach with current chart
  const handleExportPDF = async () => {
    if (!analysis || !trace) {
      alert('Please complete the analysis and tracing first before exporting the report.');
      return;
    }

    // Prevent duplicate exports
    if (isExportingPDF) {
      console.log('PDF export already in progress, skipping...');
      return;
    }

    // Prevent rapid clicking (debounce)
    const now = Date.now();
    if (now - lastExportTime < 2000) { // 2 second cooldown
      console.log('PDF export cooldown active, please wait...');
      return;
    }
    setLastExportTime(now);

    setIsExportingPDF(true);
    
    try {
      console.log('=== PDF EXPORT START ===');
      
      // Show the PDF renderer to create charts with proper dimensions
      setShowPDFRenderer(true);
      
      // Wait for the PDF renderer to be mounted
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (error) {
      console.error('Error setting up PDF export:', error);
      alert('Failed to set up PDF export. Please try again.');
      setIsExportingPDF(false);
      setShowPDFRenderer(false);
    }
  };

  // Recalculate Bitcoin and Fetch Stellar handlers removed — merged into Analyze & Trace flow

  const handleChartsReady = async (spiderElement: HTMLElement | null, flowElement: HTMLElement | null) => {
    try {
      console.log('=== CHARTS READY FOR PDF ===');
      console.log('Spider element:', !!spiderElement);
      console.log('Flow element:', !!flowElement);
      
      if (spiderElement) {
        console.log('Spider dimensions:', spiderElement.offsetWidth, 'x', spiderElement.offsetHeight);
        console.log('Spider SVG found:', !!spiderElement.querySelector('svg'));
      }
      
      if (flowElement) {
        console.log('Flow dimensions:', flowElement.offsetWidth, 'x', flowElement.offsetHeight);
        console.log('Flow SVG found:', !!flowElement.querySelector('svg'));
      }
      
      // Generate PDF report with the properly rendered charts
      await pdfExportService.generateKYTReport(
        analysis!,
        trace!,
        spiderElement,
        flowElement
      );
      
      console.log('PDF report generated successfully');
      
    } catch (error) {
      console.error('Error generating PDF report:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsExportingPDF(false);
      setShowPDFRenderer(false);
    }
  };


  // Progress indicator component
  const ProgressIndicator: React.FC = () => {
    if (!progress) return null;

    return (
      <div 
        data-progress="true"
        className="progress-indicator"
        style={{
          marginTop: 16,
          padding: 16,
          backgroundColor: '#E3F2FD',
          border: '1px solid #2196F3',
          borderRadius: 6
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#1976D2' }}>
            {progress.message}
          </span>
          <span style={{ fontSize: 12, color: '#666' }}>
            {progress.progress}%
          </span>
        </div>
        <div style={{
          width: '100%',
          height: 6,
          backgroundColor: '#E0E0E0',
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress.progress}%`,
            height: '100%',
            backgroundColor: '#2196F3',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    );
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return '#4CAF50';
      case 'MEDIUM': return '#FF9800';
      case 'HIGH': return '#F44336';
      case 'CRITICAL': return '#9C27B0';
      default: return '#9E9E9E';
    }
  };

  const formatCurrency = (amount: number, symbol: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: symbol,
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(amount);
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    <div className="kyt-dashboard-container" style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      {/* Header */}
      <div className="kyt-header" style={{ marginBottom: 24 }}>
        <h1 className="kyt-title" style={{ color: branding.primaryColor, marginBottom: 8 }}>
          KYT (Know Your Transaction) Dashboard
        </h1>
        <p className="kyt-description" style={{ color: '#666', margin: 0 }}>
          Analyze cryptocurrency addresses and trace transaction flows for compliance
        </p>
      </div>

      {/* Address Input Section */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: 12, 
        border: `1px solid ${branding.borderColor}`,
        padding: 24,
        marginBottom: 24
      }}>
        <h3 style={{ margin: '0 0 16px 0' }}>Address Analysis</h3>
        
        <div className="kyt-input-group" style={{ display: 'grid', gridTemplateColumns: '1fr 200px 120px', gap: 12, marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Enter cryptocurrency address..."
            value={currentAddress}
            onChange={(e) => {
              const value = e.target.value;
              dispatch(setCurrentAddress(value));
              const detected = detectBlockchainFromAddress(value);
              if (detected && detected !== currentBlockchain) {
                dispatch(setCurrentBlockchain(detected));
              }
            }}
            className="kyt-address-input"
            style={{
              padding: '12px 16px',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 6,
              fontSize: 14
            }}
          />
          
          <select
            value={currentBlockchain}
            onChange={(e) => dispatch(setCurrentBlockchain(e.target.value))}
            className="kyt-blockchain-select"
            style={{
              padding: '12px 16px',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 6,
              fontSize: 14
            }}
          >
            {blockchains.map(blockchain => (
              <option key={blockchain.name} value={blockchain.name}>
                {blockchain.displayName}
              </option>
            ))}
          </select>
          
          <button
            onClick={handleValidateAddress}
            disabled={isValidating || !currentAddress.trim()}
            data-loading={isValidating ? 'true' : 'false'}
            className="kyt-validate-btn"
            style={{
              padding: '12px 16px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: (isValidating || !currentAddress.trim()) ? 'not-allowed' : 'pointer',
              opacity: (isValidating || !currentAddress.trim()) ? 0.6 : 1
            }}
          >
            {isValidating ? 'Validating...' : 'Validate'}
          </button>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator />

        {/* Validation Result */}
        {validation && (
          <div className="kyt-validation-result" style={{ 
            padding: 12, 
            borderRadius: 6, 
            backgroundColor: validation.isValid ? '#E8F5E8' : '#FFEBEE',
            border: `1px solid ${validation.isValid ? '#4CAF50' : '#F44336'}`,
            marginBottom: 16
          }}>
            <div style={{ 
              color: validation.isValid ? '#2E7D32' : '#C62828',
              fontWeight: 600,
              marginBottom: 4
            }}>
              {validation.isValid ? '✅ Valid Address' : '❌ Invalid Address'}
            </div>
            <div className="kyt-validation-details" style={{ fontSize: 12, color: '#666', wordWrap: 'break-word' }}>
              Blockchain: {validation.blockchain} | 
              Detected: {validation.detectedBlockchain || 'None'}
            </div>
          </div>
        )}

        {/* Trace Steps Selector */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 14, fontWeight: 500, color: '#666' }}>
            Trace Steps:
          </label>
          <select
            value={maxSteps}
            onChange={(e) => setMaxSteps(parseInt(e.target.value))}
            disabled={isTracing || isAnalyzing}
            style={{
              padding: '8px 12px',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 6,
              fontSize: 14,
              backgroundColor: 'white',
              cursor: (isTracing || isAnalyzing) ? 'not-allowed' : 'pointer',
              opacity: (isTracing || isAnalyzing) ? 0.6 : 1,
              minWidth: 80
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7].map(step => (
              <option key={step} value={step}>
                {step} {step === 1 ? 'Step' : 'Steps'}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
            (Maximum depth for transaction tracing)
          </span>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#666' }}>
            <input
              type="checkbox"
              checked={includeZeroValue}
              onChange={(e) => setIncludeZeroValue(e.target.checked)}
              disabled={isTracing || isAnalyzing}
            />
            Raw mode (include zero-value transactions)
          </label>
        </div>

        {/* Action Buttons */}
        <div className="kyt-action-buttons" style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handleAnalyzeAddress}
            disabled={isAnalyzing || !validation?.isValid}
            className="kyt-action-btn"
            style={{
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: (isAnalyzing || !validation?.isValid) ? 'not-allowed' : 'pointer',
              opacity: (isAnalyzing || !validation?.isValid) ? 0.6 : 1
            }}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze Address'}
          </button>
          
          <button
            onClick={handleTraceTransactions}
            disabled={isTracing || isAnalyzing || !currentAddress.trim()}
            data-loading={(isTracing || isAnalyzing) ? 'true' : 'false'}
            className="kyt-action-btn"
            style={{
              padding: '12px 24px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: (isTracing || isAnalyzing || !currentAddress.trim()) ? 'not-allowed' : 'pointer',
              opacity: (isTracing || isAnalyzing || !currentAddress.trim()) ? 0.6 : 1
            }}
          >
            {isTracing
              ? 'Analyzing & Tracing...'
              : `Analyze & Trace (${maxSteps} ${maxSteps === 1 ? 'Step' : 'Steps'}${includeZeroValue ? ', Raw' : ''})`}
          </button>
          
          <button
            onClick={handleExportPDF}
            disabled={isExportingPDF || !analysis || !trace}
            className="kyt-action-btn"
            style={{
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: (isExportingPDF || !analysis || !trace) ? 'not-allowed' : 'pointer',
              opacity: (isExportingPDF || !analysis || !trace) ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {isExportingPDF ? (
              <>
                <div style={{
                  width: 16,
                  height: 16,
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Generating PDF... (Both Charts)
              </>
            ) : (
              <>
                📄 Download PDF Report (Both Charts)
              </>
            )}
          </button>
          
          {/* Removed BTC and Stellar utility buttons; logic runs inside Analyze & Trace */}
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ 
            marginTop: 16,
            padding: 12, 
            backgroundColor: '#FFEBEE',
            border: '1px solid #F44336',
            borderRadius: 6,
            color: '#C62828'
          }}>
            {error}
            <button
              onClick={() => dispatch(clearError())}
              style={{
                marginLeft: 12,
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #F44336',
                borderRadius: 4,
                color: '#F44336',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Search History */}
        {searchHistory.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 14, color: '#666' }}>Recent Searches</h4>
              <button
                onClick={() => dispatch(clearAllData())}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#fff',
                  border: '1px solid #E0E0E0',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: '#666'
                }}
              >
                Clear all data
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {searchHistory.slice(0, 5).map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    dispatch(setCurrentAddress(item.address));
                    dispatch(setCurrentBlockchain(item.blockchain));
                  }}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#F5F5F5',
                    border: '1px solid #E0E0E0',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#666'
                  }}
                >
                  {item.address.substring(0, 8)}...{item.address.substring(item.address.length - 6)} ({item.blockchain})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="kyt-tabs" style={{ 
        display: 'flex', 
        borderBottom: `2px solid ${branding.borderColor}`,
        marginBottom: 24,
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch'
      } as React.CSSProperties}>
        {[
          { id: 'analyze', label: 'Address Analysis' },
          { id: 'trace', label: 'Transaction Trace' },
          { id: 'history', label: 'Transaction History' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="kyt-tab-btn"
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: activeTab === tab.id ? branding.primaryColor : branding.textColor,
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${branding.primaryColor}` : '2px solid transparent',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: activeTab === tab.id ? 600 : 400,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Analysis Results */}
      {activeTab === 'analyze' && analysis && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: 12, 
          border: `1px solid ${branding.borderColor}`,
          padding: 24
        }}>
          <h3 style={{ margin: '0 0 20px 0' }}>Analysis Results</h3>
          
          {/* Risk Score Card */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 20, 
            marginBottom: 24 
          }}>
            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Risk Score</h4>
              <div style={{ 
                fontSize: 32, 
                fontWeight: 600, 
                color: getRiskLevelColor(analysis.riskLevel)
              }}>
                {analysis.riskScore}%
              </div>
              <div style={{ 
                fontSize: 14, 
                color: getRiskLevelColor(analysis.riskLevel),
                fontWeight: 600
              }}>
                {analysis.riskLevel}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Address Type</h4>
              <div style={{ fontSize: 18, fontWeight: 600, color: branding.primaryColor }}>
                {analysis.addressType}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {analysis.isExchange ? `Exchange: ${analysis.exchangeName}` : 'Regular Address'}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Sanctions Status</h4>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 600, 
                color: analysis.isSanctioned ? '#F44336' : '#4CAF50'
              }}>
                {analysis.isSanctioned ? 'SANCTIONED' : 'CLEAR'}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Transaction Count</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {analysis.transactionCount.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: 16, 
            marginBottom: 24 
          }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Received</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {formatCurrency(analysis.totalReceived)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Sent</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {formatCurrency(analysis.totalSent)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Net Balance</div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 600,
                color: analysis.netBalance >= 0 ? '#4CAF50' : '#F44336'
              }}>
                {formatCurrency(analysis.netBalance)}
              </div>
            </div>
          </div>

          {/* Risk Factors */}
          {analysis.riskFactors && Object.keys(analysis.riskFactors).length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 12px 0' }}>Risk Factors</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {analysis.riskFactors && Object.entries(analysis.riskFactors).map(([key, value]) => (
                  <span
                    key={key}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: '#E3F2FD',
                      color: '#1976D2',
                      borderRadius: 16,
                      fontSize: 12,
                      fontWeight: 500
                    }}
                  >
                    {key}: {String(value)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transaction Trace Results */}
      {activeTab === 'trace' && trace && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: 12, 
          border: `1px solid ${branding.borderColor}`,
          padding: 24
        }}>
          <h3 style={{ margin: '0 0 20px 0' }}>Transaction Flow Visualization</h3>
          
          {/* Trace Summary */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: 16, 
            marginBottom: 24 
          }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Trace Steps</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: branding.primaryColor }}>
                {trace.actualSteps} / {trace.maxSteps}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Volume</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {formatCurrency(trace.totalAmount)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Risk Score</div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 600,
                color: getRiskLevelColor(trace.riskScore > 70 ? 'HIGH' : trace.riskScore > 30 ? 'MEDIUM' : 'LOW')
              }}>
                {trace.riskScore}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Duration</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {Math.round((new Date(trace.endTime).getTime() - new Date(trace.startTime).getTime()) / 1000)}s
              </div>
            </div>
          </div>

          {/* Risk Alerts */}
          {(trace.highRiskAddresses.length > 0 || trace.sanctionsAddresses.length > 0) && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 12px 0' }}>Risk Alerts</h4>
              
              {trace.sanctionsAddresses.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ 
                    padding: 12, 
                    backgroundColor: '#FFEBEE',
                    border: '1px solid #F44336',
                    borderRadius: 6,
                    marginBottom: 8
                  }}>
                    <div style={{ color: '#C62828', fontWeight: 600, marginBottom: 4 }}>
                      🚨 SANCTIONS ALERT: {trace.sanctionsAddresses.length} sanctioned addresses found
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {trace.sanctionsAddresses.slice(0, 3).join(', ')}
                      {trace.sanctionsAddresses.length > 3 && '...'}
                    </div>
                  </div>
                </div>
              )}

              {trace.highRiskAddresses.length > 0 && (
                <div style={{ 
                  padding: 12, 
                  backgroundColor: '#FFF3E0',
                  border: '1px solid #FF9800',
                  borderRadius: 6
                }}>
                  <div style={{ color: '#E65100', fontWeight: 600, marginBottom: 4 }}>
                    ⚠️ HIGH RISK: {trace.highRiskAddresses.length} high-risk addresses identified
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {trace.highRiskAddresses.slice(0, 3).join(', ')}
                    {trace.highRiskAddresses.length > 3 && '...'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Interactive Transaction Flow Diagrams */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Transaction Flow Visualization</h4>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 16px 0' }}>
              Interactive visualization showing money flow in and out of addresses. 
              Click on nodes to see details, use controls to zoom and pan.
            </p>
            
            {/* Diagram Type Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: 8, 
              marginBottom: 16,
              borderBottom: `1px solid ${branding.borderColor}`
            }}>
              <button
                onClick={() => setDiagramTab('spider')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: diagramTab === 'spider' ? branding.primaryColor : 'transparent',
                  color: diagramTab === 'spider' ? 'white' : branding.textColor,
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                🕸️ Spider Network
              </button>
              <button
                onClick={() => setDiagramTab('flow')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: diagramTab === 'flow' ? branding.primaryColor : 'transparent',
                  color: diagramTab === 'flow' ? 'white' : branding.textColor,
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                🌊 Flow Diagram
              </button>
            </div>

            {/* Diagram Content - Always render both for PDF export */}
            <div style={{ 
              display: 'block',
              visibility: diagramTab === 'spider' ? 'visible' : 'hidden',
              position: diagramTab === 'spider' ? 'static' : 'absolute',
              left: diagramTab === 'spider' ? 'auto' : '-9999px'
            }} data-diagram="spider">
              <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px 0' }}>
                <strong>Spider Network:</strong> Shows all connections in a radial layout. 
                Best for understanding the complete network structure and relationships.
              </p>
              <TransactionFlowDiagram traceData={trace} />
            </div>
            
            <div style={{ 
              display: 'block',
              visibility: diagramTab === 'flow' ? 'visible' : 'hidden',
              position: diagramTab === 'flow' ? 'static' : 'absolute',
              left: diagramTab === 'flow' ? 'auto' : '-9999px'
            }} data-diagram="flow">
              <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px 0' }}>
                <strong>Flow Diagram:</strong> Shows transaction flow from root address in levels. 
                Best for understanding the sequential flow and distance from the root.
              </p>
              <CryptoTransactionFlow traceData={trace} />
            </div>
          </div>

          {/* Enhanced Detailed Trace Steps */}
          {trace.traceSteps && trace.traceSteps.length > 0 && (
            <KYTTraceSteps 
              traceSteps={trace.traceSteps} 
              rootAddress={trace.rootAddress}
              loading={isTracing}
            />
          )}
        </div>
      )}

      {/* Transaction History */}
      {activeTab === 'history' && analysis && (
        <KYTTransactionHistory 
          transactions={analysis.transactions || []} 
          loading={isAnalyzing}
        />
      )}
    </div>

    {/* PDF Chart Renderer - Hidden container for PDF export */}
    {showPDFRenderer && (
      <PDFChartRenderer
        traceData={trace}
        onChartsReady={handleChartsReady}
      />
    )}
    </>
  );
};

export default KYTDashboard;
